import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { useRouteSession } from '@web/app'
import { authClient } from '@web/auth'
import { Alert, AlertDescription } from '@layerit/ui/components/ui/alert'
import { Button } from '@layerit/ui/components/ui/button'
import { Field, FieldError, FieldLabel } from '@layerit/ui/components/ui/field'
import { Input } from '@layerit/ui/components/ui/input'
import { ORGANIZATIONS_QUERY_KEY } from '@web/hooks/organizations'
import { resetTenantQueries } from '@web/query'
import { ROUTES } from '@web/router'

export function slugFrom(name: string): string {
	return name
		.toLowerCase()
		.normalize('NFKD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 48)
}

const organisationFormSchema = z.object({
	name: z.string().trim().min(1, 'Enter the organisation name.'),
})

type OrganisationFormValues = z.infer<typeof organisationFormSchema>

interface CreateOrganisationFormProps {
	redirectTo?: string
}

export function CreateOrganisationForm({ redirectTo = ROUTES.getStarted }: CreateOrganisationFormProps) {
	const navigate = useNavigate()
	const { reload } = useRouteSession()
	const queryClient = useQueryClient()

	const form = useForm<OrganisationFormValues>({
		resolver: zodResolver(organisationFormSchema),
		defaultValues: { name: '' },
	})
	const name = useWatch({ control: form.control, name: 'name' })
	const slug = slugFrom(name)

	const create = useMutation({
		mutationFn: async (organisation: string) =>
			authClient.organization.create({ name: organisation, slug: slugFrom(organisation) || 'organisation' }),
		onSuccess: async (result) => {
			if (result.error) return
			if (result.data?.id) await authClient.organization.setActive({ organizationId: result.data.id })
			await resetTenantQueries(queryClient)
			await queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY })
			await reload()
			await navigate(redirectTo, { replace: true })
		},
	})

	const canCreate = name.trim().length > 0 && !create.isPending

	function submit({ name: organisation }: OrganisationFormValues) {
		create.mutate(organisation)
	}

	return (
		<form className="flex flex-col" noValidate onSubmit={(event) => void form.handleSubmit(submit)(event)}>
			<Controller
				control={form.control}
				name="name"
				render={({ field, fieldState }) => (
					<Field className="gap-2" data-invalid={fieldState.invalid || create.data?.error != null}>
						<FieldLabel htmlFor="organisation">Organisation name</FieldLabel>
						<Input
							{...field}
							id="organisation"
							required
							autoFocus
							placeholder="Acme Ltd"
							className="h-11"
							aria-invalid={fieldState.invalid || create.data?.error != null}
						/>
						<p className="text-xs text-ink-3">
							{slug ? (
								<>
									Your signature pages will live under <span className="record text-ink-2">/{slug}</span>. You will be its
									owner.
								</>
							) : (
								'You will be its owner, and you can invite everybody else afterwards.'
							)}
						</p>
						{fieldState.invalid && <FieldError errors={[fieldState.error]} />}
					</Field>
				)}
			/>

			{create.data?.error && (
				<Alert variant="destructive" className="mt-5">
					<AlertDescription>{create.data.error.message ?? 'That organisation could not be created.'}</AlertDescription>
				</Alert>
			)}

			<Button type="submit" variant="primary" className="mt-7 h-11" disabled={!canCreate}>
				{create.isPending ? 'Creating…' : 'Create and continue'}
			</Button>
		</form>
	)
}
